import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '@/redux'
import { loadChatSession } from '@/services/DashboardChatService'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'
import { DashboardCanvas } from './Canvas'
import { DashboardCreationShell } from './DashboardCreationShell'
import { Loading } from '@/components/custom/Loading/Loading'

/**
 * DashboardByIdRoute — smart router mounted at `/dashboards/:id`.
 *
 * Reads `flowState` from Redux once the session is hydrated and decides
 * which component to render:
 *
 *   - Not yet hydrated (initial load before loadChatSession resolves) → loading spinner
 *   - flowState === 'complete' → <DashboardCanvas />
 *   - Any other flowState → <DashboardCreationShell /> (resume path via bootstrap)
 *
 * The `/dashboards/new` route is left unchanged and still mounts
 * <DashboardCreationShell /> directly for the create-new path.
 *
 * Hydration tracking: we use a local `isHydrated` flag that flips true once the
 * loadChatSession thunk resolves AND the chat session is observably loaded
 * (sessionId present + isLoading === false). Just looking at `sliceDashboardId`
 * is insufficient because the effect itself synchronously dispatches
 * setDashboardId, so the next render sees `sliceDashboardId === id` before the
 * async session load has resolved (PR #331 review).
 */
export function DashboardByIdRoute() {
  const dispatch: AppDispatch = useDispatch()
  const { id } = useParams<{ id: string }>()
  const { token } = useSelector((state: RootState) => state.user)
  const flowState = useSelector((state: RootState) => state.dashboardCreation.flowState)
  const creationStatus = useSelector((state: RootState) => state.chatSession.creationStatus)
  const sliceDashboardId = useSelector((state: RootState) => state.dashboardCreation.dashboardId)
  const sessionId = useSelector((state: RootState) => state.chatSession.sessionId)

  // isHydrated: true once loadChatSession has resolved (or if we had a same-session
  // cache hit, identified by both dashboardId match AND a non-empty sessionId).
  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    if (!token || !id) return
    let mounted = true

    // Reset hydration when the route id changes (prevents stale 'true' carrying
    // over to a new dashboard).
    setIsHydrated(false)

    // Same-session cache hit: the slice already holds this dashboardId AND a
    // chat session has actually been loaded for it (non-empty sessionId).
    // Without the sessionId guard, the cache-hit branch fires after the effect
    // synchronously seeds the id but before the async load resolves, removing
    // the spinner while flowState is still at its default.
    //
    // NOTE: sessionId is intentionally read from closure but NOT listed in deps.
    // Canvas.tsx Effect 1 dispatches clearSession() on mount, which sets sessionId
    // to null. If sessionId were in deps, that clearSession() would re-trigger this
    // effect → setIsHydrated(false) → Canvas unmounts → Canvas cleanup calls
    // clearSession() again → infinite remount loop (~482 requests/33s observed
    // during bugbash 2026-04-30). We only need to re-run when the route id, token,
    // or the stored dashboard id changes — not on every sessionId mutation.
    if (sliceDashboardId === id && (sessionId?.trim().length ?? 0) > 0) {
      setIsHydrated(true)
      return
    }

    // Cold load (hard-refresh or direct URL). Seed the id and trigger a hydration.
    // DashboardCreationShell's bootstrap (useDashboardCreationBootstrap) will also
    // call loadChatSession via the with-id path when it mounts — that's fine because
    // loadChatSession is idempotent (it replaces state). We dispatch here too so
    // `DashboardCanvas` (complete path) hydrates without mounting the shell.
    dispatch(dashboardCreationActions.setDashboardId({ dashboardId: id }))
    void (dispatch(loadChatSession(token, id)) as unknown as Promise<void>).then(() => {
      if (mounted) setIsHydrated(true)
    })

    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, id, sliceDashboardId, dispatch])

  if (!isHydrated) {
    return <Loading />
  }

  // Resolve "is this dashboard already built?" with a fallback chain.
  // Primary: explicit FE flowState. Fallback: chatSession.creationStatus —
  // legacy/pre-deploy sessions hydrate without flowState, but creationStatus is
  // always present and 'complete' there means the dashboard was finalized
  // (PR #331 Qodo review §7).
  const isComplete = flowState === 'complete' || creationStatus === 'complete'

  if (isComplete) {
    return <DashboardCanvas />
  }

  return <DashboardCreationShell />
}
